import type { DocumentLanguage } from "./types.ts";

/**
 * FIN-1580: the shared bilingual content catalog for invoice-DOCUMENT content (headings,
 * line-item labels, payment-box labels) — not UI chrome (that's the separate, in-flight
 * `packages/i18n`/platform-i18n effort, out of scope per specs/invoice-document-i18n/
 * requirements.md's "Suggested solution"). English is the source of truth; every key has
 * both languages so a missing one is a deliberate gap, not an oversight (EC9).
 *
 * Consumed by the Deno PDF renderer (via the re-export shim
 * supabase/functions/_shared/tax/invoiceCatalog/index.ts) and by both apps' live preview —
 * the single place a string can be edited so the two can never drift apart again (AC1).
 */
export const DOCUMENT_STRINGS = {
  "heading.title": { sv: "Kundfaktura", en: "Invoice" },
  "heading.invoiceNumber": { sv: "Fakturanummer", en: "Invoice number" },
  "heading.invoiceDate": { sv: "Fakturadatum", en: "Invoice date" },
  "heading.dueDate": { sv: "Förfallodatum", en: "Due date" },
  "heading.dueImmediately": { sv: " (betalas omgående)", en: " (due immediately)" },
  "customer.heading": { sv: "KUND", en: "CUSTOMER" },
  "customer.none": { sv: "Ingen kund vald", en: "No customer selected" },
  "customer.vatNumber": { sv: "Momsnr:", en: "VAT:" },
  "customer.ourReference": { sv: "Vår referens:", en: "Our reference:" },
  "customer.theirReference": { sv: "Er referens:", en: "Your reference:" },
  "table.description": { sv: "Beskrivning", en: "Description" },
  "table.qty": { sv: "Antal", en: "Qty" },
  "table.unitPrice": { sv: "Á-pris", en: "Unit price" },
  "table.vat": { sv: "Moms", en: "VAT" },
  "table.amount": { sv: "Belopp", en: "Amount" },
  "table.empty": { sv: "Inga fakturarader ännu", en: "No invoice lines yet" },
  "totals.net": { sv: "Belopp exkl. moms", en: "Amount excl. VAT" },
  "totals.rounding": { sv: "Öresavrundning", en: "Rounding" },
  "totals.toPay": { sv: "Att betala", en: "Total due" },
  "seller.orgNumber": { sv: "Org.nr:", en: "Org. no:" },
  "seller.vatNumber": { sv: "Momsreg.nr:", en: "VAT no:" },
  "seller.fSkatt": { sv: "Godkänd för F-skatt", en: "Approved for F-tax" },
  "payment.bankgiroHeading": { sv: "INBETALNING", en: "PAYMENT" },
  "payment.bankAccountHeading": { sv: "BETALA TILL BANKKONTO", en: "PAY TO BANK ACCOUNT" },
  "payment.ocrLabel": { sv: "OCR-nummer", en: "OCR number" },
  "payment.messageLabel": { sv: "Meddelande", en: "Message" },
  "payment.amountDueLabel": { sv: "Belopp att betala", en: "Amount due" },
  "payment.fromAbroad": { sv: "Från utlandet", en: "From abroad" },
  "payment.noOcrYet": { sv: "— (sätts vid skapande)", en: "— (assigned when created)" },
  "payment.bankLabel": { sv: "Bank", en: "Bank" },
  "payment.clearingNumberLabel": { sv: "Clearingnummer", en: "Clearing number" },
  "payment.accountNumberLabel": { sv: "Kontonummer", en: "Account number" },
  "overflow.pageFull": {
    sv: "Fakturan får inte plats på en sida — korta meddelandet eller dela upp fakturaraderna.",
    en: "The invoice doesn't fit on one page — shorten the message or split the invoice lines.",
  },
} as const satisfies Record<string, Record<DocumentLanguage, string>>;

export type DocumentStringKey = keyof typeof DOCUMENT_STRINGS;

/** Fired (never thrown) when a key is missing a language entry — can't happen for a key in
 * DOCUMENT_STRINGS today (every key has both), kept for a future key added without both. */
export type MissingCatalogKeyAlert = (key: string, requestedLanguage: DocumentLanguage) => void;

let onMissingKey: MissingCatalogKeyAlert = () => {};

/** Lets a consumer (the Deno shim, a test) wire up its own alerting without this package
 * depending on either runtime's logging. */
export function setMissingCatalogKeyHandler(handler: MissingCatalogKeyAlert): void {
  onMissingKey = handler;
}

/**
 * Resolves a document string for the invoice's own language (EC9: a missing key falls back to
 * English and alerts, never throws or renders a raw key — the PDF/preview must always render).
 */
export function t(key: DocumentStringKey, language: DocumentLanguage): string {
  const entry = DOCUMENT_STRINGS[key];
  if (!entry) {
    onMissingKey(key, language);
    return key;
  }
  const value = (entry as Record<DocumentLanguage, string>)[language] ?? entry.en;
  if (value === undefined) {
    onMissingKey(key, language);
    return entry.en ?? key;
  }
  return value;
}
